import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fadeUp, stagger } from "@/lib/motion";
import { cn } from "@/lib/utils";

const PLANS = [
  {
    id: "silver",
    name: "Silver",
    tagline: "TallyPrime Single User",
    monthly: 22500,
    yearly: 22500,
    highlight: false,
    features: [
      "1 User license (Perpetual)",
      "GST, e-Invoicing, e-Way Bills",
      "Basic inventory & invoicing",
      "Free installation",
      "3 months email support",
    ],
    cta: "Choose Plan",
  },
  {
    id: "gold",
    name: "Gold",
    tagline: "TallyPrime Multi User",
    monthly: 67500,
    yearly: 67500,
    highlight: true,
    features: [
      "Unlimited users on LAN",
      "All GST & compliance modules",
      "Advanced inventory & payroll",
      "Priority phone + WhatsApp support",
      "1 year of upgrades & AMC",
    ],
    cta: "Choose Plan",
  },
  {
    id: "rental",
    name: "Rental",
    tagline: "Pay monthly, cancel anytime",
    monthly: 750,
    yearly: 7500,
    highlight: false,
    features: [
      "TallyPrime on subscription",
      "Includes updates & upgrades",
      "Cloud-ready configuration",
      "24x7 remote support",
      "Zero upfront cost",
    ],
    cta: "Choose Plan",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Custom for large teams",
    monthly: null,
    yearly: null,
    highlight: false,
    features: [
      "Multi-location & multi-company",
      "TallyPrime Server + On-premise",
      "Custom integrations & TDL",
      "Dedicated account manager",
      "Enterprise AMC & SLA",
    ],
    cta: "Choose Plan",
  },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

export default function Pricing() {
  const [billing, setBilling] = useState("yearly");

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 lg:py-32 bg-[#FAF9F6] overflow-hidden">
      {/* Floating orb */}
      <div className="orb bg-[#0D47A1] w-[500px] h-[500px] top-24 -left-40 opacity-10" />
      <div className="orb bg-[#FFA000] w-[400px] h-[400px] bottom-0 -right-32 opacity-10" style={{ animationDelay: "-6s" }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center max-w-2xl mx-auto"
        >
          <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Pricing Plans</motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A2E]"
          >
            Simple pricing.{" "}
            <span className="font-serif-accent text-[#FFA000]">Zero surprises.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-[17px] text-[#4A5568]">
            Choose the TallyPrime edition that fits your team. Every plan includes installation, updates and expert onboarding.
          </motion.p>

          {/* Billing toggle */}
          <motion.div variants={fadeUp} className="mt-8 inline-flex items-center gap-1 p-1 rounded-lg border border-[#0D47A1]/15 bg-white shadow-[0_4px_20px_rgba(13,71,161,0.06)]">
            {[
              { id: "monthly", label: "Monthly" },
              { id: "yearly", label: "One-time" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setBilling(opt.id)}
                data-testid={`pricing-toggle-${opt.id}`}
                className={cn(
                  "px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-300",
                  billing === opt.id
                    ? "bg-[#0D47A1] text-white shadow-sm"
                    : "text-[#4A5568] hover:text-[#1A1A2E]"
                )}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          variants={stagger(0.08, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {PLANS.map((plan) => (
            <motion.div
              key={plan.id}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`pricing-card-${plan.id}`}
              className={cn(
                "relative rounded-2xl p-7 flex flex-col transition-all duration-500",
                plan.highlight
                  ? "bg-[#FFA000] text-white border-2 border-[#FFA000] shadow-[0_30px_80px_rgba(255,160,0,0.35)] lg:-translate-y-4"
                  : "bg-white text-[#1A1A2E] border border-[#0D47A1]/10 shadow-[0_8px_30px_rgba(13,71,161,0.06)] hover:shadow-[0_20px_50px_rgba(13,71,161,0.1)]"
              )}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#0D47A1] text-white px-4 py-1 text-[11px] font-bold tracking-[0.14em] uppercase flex items-center gap-1.5 shadow-lg">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
              )}
              <div>
                <div className={cn("text-xs font-bold tracking-[0.14em] uppercase", plan.highlight ? "text-white/80" : "text-[#0D47A1]")}>
                  {plan.name}
                </div>
                <p className={cn("mt-1 text-sm", plan.highlight ? "text-white/80" : "text-[#8896A6]")}>{plan.tagline}</p>

                <div className="mt-6 flex items-baseline gap-1">
                  {plan.monthly ? (
                    <>
                      <span className="font-display font-black text-5xl tracking-tight">
                        {fmt(billing === "monthly" ? plan.monthly : plan.yearly)}
                      </span>
                      <span className={cn("text-sm", plan.highlight ? "text-white/80" : "text-[#8896A6]")}>
                        {plan.id === "rental" ? (billing === "monthly" ? "/mo" : "/yr") : "+18% GST"}
                      </span>
                    </>
                  ) : (
                    <span className="font-display font-black text-5xl tracking-tight">Custom</span>
                  )}
                </div>
              </div>

              <ul className={cn("mt-7 space-y-3 flex-1", plan.highlight ? "text-white/95" : "text-[#1A1A2E]/85")}>
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[14px]">
                    <Check className={cn("w-4.5 h-4.5 mt-0.5 shrink-0", plan.highlight ? "text-white" : "text-[#FFA000]")} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => scrollTo("#contact")}
                data-testid={`pricing-cta-${plan.id}`}
                className={cn(
                  "btn-magnetic mt-8 w-full rounded-lg h-12 text-[14px] font-bold",
                  plan.highlight
                    ? "bg-white hover:bg-[#F5F7FA] text-[#FFA000] shadow-[0_10px_30px_rgba(0,0,0,0.15)]"
                    : "bg-[#0D47A1] hover:bg-[#0A3680] text-white shadow-[0_10px_30px_rgba(13,71,161,0.3)]"
                )}
              >
                {plan.cta}
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
